import {
  createContext,
  FC,
  useReducer,
  Dispatch,
  useEffect,
  useState,
} from "react";
import { Object3D } from "three";
import content from "../content.json";
import { audioContext, uiAudio } from "../App";
import { BIOMEMASTER_UNLOCK_COUNT, RARE_PLANT_TOTAL_COUNT } from "../CONSTANTS";
import ScreenSpacePosition from "../scene/ScreenSpacePosition";
import DeviceGradeDetection from "../util/DeviceGradeDetection";
import Eventstream from "../lib/eventstream/eventstream.js";

const EVENT_STREAM_IDENTIFIER = "cbca-2022";

export enum World {
  "FungiForest",
  "FieldTrip",
  "CoralLagoon",
}

export enum ViewMode {
  "Intro",
  "LandingPage",
  "World",
  "Lookbook",
}

export enum ModalType {
  "Plant",
  "BiomeMaster",
  "Look",
  "Manifesto",
}

export enum ControlsMode {
  "Pointerlock",
  "Joystick",
}

export interface Modal {
  type: ModalType;
  name?: string;
}

export enum FocusedObjectIcon {
  "FungiForest",
  "FieldTrip",
  "CoralLagoon",
  "Eye",
}

export enum FocusedObjectType {
  "Look",
  "World",
  "Plant",
}

export interface FocusedObject {
  object: Object3D;
  screenSpacePosition: ScreenSpacePosition;
  name: string;
  icon: FocusedObjectIcon;
  type: FocusedObjectType;
}

interface AppState {
  isInit: boolean;
  isMenuOpen: boolean;
  userHasStarted: boolean;
  isWorldLoading: boolean;
  isWorldDissolving: boolean;
  isViewerLoading: boolean;
  isMuted: boolean;
  userToggledMute: boolean;
  loadProgress: number;
  world: World | null;
  viewMode: ViewMode;
  isMainCanvasPaused: boolean;
  modal: Modal | null;
  manifestoSlide: number | null;
  focusedObject: FocusedObject[];
  isPointerLocked: boolean;
  showControlsMoveHint: boolean;
  controlsMoveHintWasShown: boolean;
  unlockedPlants: string[];
  plantPower: { current: number; previous: number };
  isBiomeMasterUnlocked: boolean;
  discountCode: string | null;
  controlsMode: ControlsMode;
}

export enum ActionType {
  "OnInit",
  "ToggleMenu",
  "SetIsWorldLoading",
  "SetIsWorldDissolving",
  "SetIsViewerLoading",
  "ToggleMute",
  "SetWorld",
  "SetViewMode",
  "SetModal",
  "SetManifestoSlide",
  "SetFocusedObject",
  "SetPointerLocked",
  "HideControlsHint",
  "HitPlant",
  "LoadProgress",
  "DiscountCodeRecieved",
}

interface Action {
  type: ActionType;
  value?: any;
}

const reducer = (state: AppState, action: Action) => {
  switch (action.type) {
    case ActionType.OnInit:
      return { ...state, isInit: true };
    case ActionType.ToggleMenu:
      return { ...state, isMenuOpen: !state.isMenuOpen };
    case ActionType.SetIsWorldLoading:
      return {
        ...state,
        isWorldLoading: action.value,
        loadProgress: action.value ? 0 : state.loadProgress,
      };
    case ActionType.SetIsWorldDissolving:
      return {
        ...state,
        isWorldDissolving: action.value,
      };
    case ActionType.SetIsViewerLoading:
      return {
        ...state,
        isViewerLoading: action.value,
        loadProgress: action.value ? 0 : state.loadProgress,
      };
    case ActionType.ToggleMute:
      return {
        ...state,
        isMuted: !state.isMuted,
        userToggledMute: true,
      };
    case ActionType.LoadProgress:
      return {
        ...state,
        loadProgress: action.value,
      };
    case ActionType.SetWorld:
      return {
        ...state,
        world: action.value,
        showControlsMoveHint: state.controlsMode === ControlsMode.Joystick,
        viewMode: action.value === null ? ViewMode.LandingPage : ViewMode.World,
        isWorldLoading: !!World[action.value] ? true : false,
        loadProgress: action.value ? 0 : state.loadProgress,
        isMuted:
          !state.userHasStarted && !state.userToggledMute
            ? false
            : state.isMuted, // first time user starts, unmute.
        userHasStarted: true,
      };
    case ActionType.SetViewMode:
      return {
        ...state,
        modal: null,
        viewMode: action.value,
        world: action.value === ViewMode.World ? state.world : null,
        isMuted:
          !state.userHasStarted && !state.userToggledMute
            ? false
            : state.isMuted, // first time user starts, unmute.
        userHasStarted: true,
      };
    case ActionType.SetModal:
      if (
        state.modal?.type === ModalType.Plant &&
        !action.value &&
        state.isBiomeMasterUnlocked
      ) {
        return {
          ...state,
          modal: { type: ModalType.BiomeMaster },
          manifestoSlide: null,
          isPointerLocked: false,
        };
      } else {
        return {
          ...state,
          modal: action.value,
          manifestoSlide: null,
          isPointerLocked: false,
          isMainCanvasPaused: action.value?.type === ModalType.Look,
        };
      }
    case ActionType.SetManifestoSlide:
      return { ...state, manifestoSlide: action.value };
    case ActionType.SetFocusedObject:
      if (action.value) {
        return {
          ...state,
          focusedObject: [...state.focusedObject, action.value],
        };
      }
      return {
        ...state,
        focusedObject: [],
      };
    case ActionType.SetPointerLocked:
      return {
        ...state,
        isPointerLocked: action.value,
        showControlsMoveHint: action.value && !state.controlsMoveHintWasShown,
        controlsMoveHintWasShown:
          action.value === true ? true : state.controlsMoveHintWasShown,
      };
    case ActionType.HideControlsHint:
      if (!state.showControlsMoveHint) {
        return state;
      }
      return { ...state, showControlsMoveHint: false };
    case ActionType.HitPlant:
      if (state.unlockedPlants.indexOf(action.value) > -1) {
        return {
          ...state,
          plantPower: {
            previous: state.plantPower.current,
            current: state.plantPower.current,
          },
        };
      }
      const newUnlockedPlants = [...state.unlockedPlants, action.value];
      return {
        ...state,
        unlockedPlants: newUnlockedPlants,
        plantPower: {
          previous: state.plantPower.current,
          current: newUnlockedPlants.length / RARE_PLANT_TOTAL_COUNT,
        },
        isBiomeMasterUnlocked:
          newUnlockedPlants.length >= BIOMEMASTER_UNLOCK_COUNT &&
          content.showDiscountCode,
      };
    case ActionType.DiscountCodeRecieved:
      return { ...state, discountCode: action.value };
    default:
      return state;
  }
};

const initialState: AppState = {
  isInit: false,
  isMenuOpen: false,
  userHasStarted: false,
  isWorldLoading: false,
  isWorldDissolving: false,
  isViewerLoading: false,
  isMuted: true,
  userToggledMute: false,
  loadProgress: 0,
  world: null,
  viewMode: ViewMode.LandingPage,
  isMainCanvasPaused: false,
  modal: null,
  manifestoSlide: null,
  focusedObject: [],
  isPointerLocked: false,
  showControlsMoveHint: true,
  controlsMoveHintWasShown: false,
  unlockedPlants: [],
  plantPower: { current: 0, previous: 0 },
  isBiomeMasterUnlocked: false,
  discountCode: null,
  controlsMode: DeviceGradeDetection.isMobile
    ? ControlsMode.Joystick
    : ControlsMode.Pointerlock,
};

const urlParams = new URLSearchParams(window.location.search);
if (urlParams.get("page") === "lookbook") {
  initialState.viewMode = ViewMode.Lookbook;
  initialState.world = null;
  window.history.replaceState(null, null, "/");
}

export const AppContext = createContext<{
  state?: AppState;
  dispatch?: Dispatch<Action>;
}>({ state: {} as AppState, dispatch: () => null });

export const AppProvider: FC = (props) => {
  // const [isAnalyticsInit, setIsAnalyticsInit] = useState(false);
  const [eventStream, setEventStream] = useState(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isBiomeMasterUnlocked, isMuted, modal, world, viewMode } = state;

  useEffect(() => {
    (window as any).dispatch = (params) => dispatch(params);
    (window as any).state = state;
  }, []);

  useEffect(() => {
    if (isBiomeMasterUnlocked) {
      const list = "ABCDEFGHIJKLMNOPQRSTUVWUXY".split("");
      const value = `FN${String(Date.now()).substr(-8)}${
        list[Math.floor(Math.random() * list.length)]
      }${list[Math.floor(Math.random() * list.length)]}${
        list[Math.floor(Math.random() * list.length)]
      }`;
      dispatch({
        type: ActionType.DiscountCodeRecieved,
        value,
      });
    }
  }, [isBiomeMasterUnlocked]);

  useEffect(() => {
    if (isMuted) {
      audioContext.suspend();
      uiAudio.isMuted = true;
    } else {
      if (audioContext.state === "suspended") {
        audioContext.resume();
        uiAudio.isMuted = false;
      }
    }
  }, [isMuted]);

  useEffect(() => {
    if (modal) {
      switch (modal.type) {
        case ModalType.Plant:
          uiAudio.play("RarePlant");
          break;
        case ModalType.Look:
          uiAudio.play("NewLook");
          break;
      }
    }
  }, [modal]);

  useEffect(() => {
    const enumName = World[world];
    if (enumName) {
      uiAudio.playSoundtrack(content.worlds[enumName].soundtrack);
    } else {
      uiAudio.playSoundtrack("Soundtrack_General");
    }
  }, [world]);

  useEffect(() => {
    const es = new Eventstream();
    setEventStream(es);
  }, []);

  useEffect(() => {
    if (!!eventStream) {
      let pageName = ViewMode[viewMode];
      if (viewMode === ViewMode.World) {
        pageName += ` (${World[world]})`;
      }
      console.info(`Pageview: ${pageName}`);
      eventStream.pageView(pageName);
    }
  }, [viewMode, world, eventStream]);

  useEffect(() => {
    if (!!eventStream && modal) {
      let name = ModalType[modal.type];
      if (modal.name) {
        name += ` (${modal.name})`;
      }
      console.info(`Modalview: ${name}`);
      eventStream.event(EVENT_STREAM_IDENTIFIER, {
        cash_web_event_description: "view-modal",
        cash_web_event_detail: name,
      });
      // ReactGA.pageview(name);
    }
  }, [modal, eventStream]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  );
};
